import AppVersion from '@/components/app-version'
import ModeToggle from '@/components/landing/mood-toggle'
import { FullPageSpinner } from '@/components/loading_states/generic-spinner'
import { useAuth } from '@/components/providers/auth-provider'
import { AppSidebar } from '@/components/sidebar/app-sidebar'
import { adminSidebarData } from '@/components/sidebar/sidebar-data'
import { Separator } from '@/components/ui/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { COLORS } from '@repo/shared_utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate, Outlet, useNavigate } from '@tanstack/react-router'
import { CalendarIcon, ShieldIcon } from 'lucide-react'
import { DateTime } from 'luxon'

export const Route = createFileRoute('/dashboard/admin')({
  component: RouteComponent,
})

function RouteComponent() {
  const { user, isPending } = useAuth()
  const { data: currentDateTime } = useQuery({
    queryKey: ['current-time'],
    queryFn: () => DateTime.now(),
    refetchInterval: 60000, // refetch every minute (60 seconds)
    staleTime: 0,
  })
  const navigate = useNavigate()

  if (isPending) return <FullPageSpinner />
  if (!user) return <Navigate to="/login" />

  if (user.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="rounded-lg bg-gradient-to-br from-background via-background/95 to-background/90 dark:from-gray-900/60 dark:to-gray-800/60 border border-border/50 px-8 py-6 shadow-md flex flex-col items-center">
          <span className="text-4xl mb-2">
            <ShieldIcon className="h-8 w-8 text-blue-700 dark:text-blue-300" />
          </span>
          <h2 className="text-xl font-semibold text-blue-700 dark:text-blue-300 mb-1">
            Access Denied
          </h2>
          <p className="text-muted-foreground text-center mb-4">
            You are not authorized to access this page.
          </p>
          <button
            className="mt-2 px-4 py-2 rounded-xl cursor-pointer bg-blue-600 text-white hover:bg-blue-700 transition"
            onClick={() => navigate({ to: `/dashboard/${user.role}` })}
            type="button"
          >
            Go to your dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <SidebarProvider>
      <AppSidebar
        {...{ ...adminSidebarData, user: user, userProfileUrl: '/dashboard/admin/user_profile' }}
      />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 bg-gradient-to-r from-background via-background/95 to-background/90 backdrop-blur-sm border-b border-border/50">
          <div className="flex w-full items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1 hover:bg-gradient-to-r hover:from-violet-50 hover:to-blue-50 dark:hover:from-violet-950/50 dark:hover:to-blue-950/50 transition-all duration-200" />
            <Separator
              orientation="vertical"
              className="mr-2 h-4 bg-gradient-to-b from-border to-border/50"
            />

            <Separator
              orientation="vertical"
              className="mr-2 h-4 bg-gradient-to-b from-border to-border/50"
            />

            <div className="ml-auto flex items-center space-x-3">
              <AppVersion />
              <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-900/50 dark:to-gray-800/50 backdrop-blur-sm border border-border/50">
                {/* <ClockIcon className="h-4 w-4" style={{ color: COLORS[2] }} /> */}
                <CalendarIcon className="h-4 w-4" style={{ color: COLORS[1] }} />
                <div className="hidden md:block text-sm font-medium bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  {currentDateTime?.toFormat('MMMM d, yyyy • h:mm a')}
                </div>
              </div>
              <div className="p-1 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 backdrop-blur-sm border border-border/50">
                <ModeToggle />
              </div>
            </div>
          </div>
        </header>
        <div className="p-6 bg-gradient-to-br from-background via-background/95 to-background/90">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
